/**
 * CarePlanService — Enterprise Care Plan Management
 *
 * Covers: 5 plan types, 18-section model, auto-generation engine,
 * review lifecycle (8-weekly + unscheduled), goal tracking, intervention
 * library integration, section locking, summary generation, facility
 * dashboard KPIs, version diffing, and full audit trail.
 */

import LocalStorageService from '../storage/LocalStorageService';
import {
  CARE_PLAN_STATUS, CARE_PLAN_TYPE, CARE_PLAN_SECTIONS, SECTION_STATUS,
  ASSESSMENT_SECTION_MAP, REVIEW_INTERVAL_MS, REVIEW_LOOKAHEAD_MS,
  RISK_LEVEL, GOAL_STATUS, INTERVENTION_STATUS, INTERVENTION_SOURCE,
  GENERATED_FROM, REVIEW_OUTCOME, EVALUATION_OUTCOME,
  SUMMARY_EXTRACTION_MAP, BSP_REVIEW_INTERVAL_WEEKS,
  createCarePlan, createCarePlanSection, createCarePlanNeed,
  createCarePlanGoal, createGoalProgress, createCarePlanIntervention,
  createCarePlanEvaluation,
} from '../../data/models/CarePlan';
import { getDefaultInterventionsForSection } from '../../data/interventionLibrary';

const STORAGE_KEY = 'mf_care_plans_v2';
const LOCKS_KEY = 'mf_care_plan_locks';
const AUDIT_KEY = 'mf_care_plan_audit';
const ASSESSMENT_INSTANCES_KEY = 'clinical_assessment_instances';
const LOCK_TTL_MS = 15 * 60 * 1000; // 15 min auto-release

const isDev = process.env.NODE_ENV === 'development';
const debug = (...args) => isDev && console.debug('[CarePlanService]', ...args); // eslint-disable-line no-console

function _uid(prefix = 'CP') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
function _now() { return new Date().toISOString(); }

class CarePlanService {
  // ── Storage helpers ───────────────────────────────────────────────────

  _getAll() { return LocalStorageService.get(STORAGE_KEY, []); }
  _saveAll(plans) { LocalStorageService.set(STORAGE_KEY, plans); }
  _getLocks() { return LocalStorageService.get(LOCKS_KEY, {}); }
  _saveLocks(locks) { LocalStorageService.set(LOCKS_KEY, locks); }

  _audit(action, details = {}) {
    const entries = LocalStorageService.get(AUDIT_KEY, []);
    entries.push({ id: _uid('AUD'), action, ...details, timestamp: _now() });
    if (entries.length > 500) entries.splice(0, entries.length - 500);
    LocalStorageService.set(AUDIT_KEY, entries);
    debug('audit', action, details);
  }

  // ── CRUD ──────────────────────────────────────────────────────────────

  createCarePlanRecord(residentId, createdBy, type = CARE_PLAN_TYPE.COMPREHENSIVE) {
    if (!residentId) return { ok: false, error: 'residentId is required' };
    if (!createdBy) return { ok: false, error: 'createdBy is required' };

    const plan = createCarePlan({ residentId, createdBy, type });
    const plans = this._getAll();
    plans.push(plan);
    this._saveAll(plans);
    this._audit('create_care_plan', { planId: plan.id, residentId, type });
    return { ok: true, carePlan: plan };
  }

  /** Alias kept for backwards compat with older callers */
  createCarePlan(residentId, createdBy) {
    return this.createCarePlanRecord(residentId, createdBy, CARE_PLAN_TYPE.COMPREHENSIVE);
  }

  getCarePlan(carePlanId) {
    return this._getAll().find(cp => cp.id === carePlanId) || null;
  }

  getCarePlansByResident(residentId) {
    const rid = String(residentId);
    return this._getAll()
      .filter(cp => String(cp.residentId) === rid)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  getActiveCarePlan(residentId, type = null) {
    const rid = String(residentId);
    return this._getAll()
      .filter(cp =>
        String(cp.residentId) === rid &&
        cp.status === CARE_PLAN_STATUS.ACTIVE &&
        (type ? cp.type === type : true)
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null;
  }

  getActiveComprehensive(residentId) { return this.getActiveCarePlan(residentId, CARE_PLAN_TYPE.COMPREHENSIVE); }

  // ── Section editing ───────────────────────────────────────────────────

  updateSection(carePlanId, sectionNumber, content, userId = 'system') {
    if (sectionNumber < 1 || sectionNumber > 18) {
      return { ok: false, error: `Invalid section number: ${sectionNumber}` };
    }
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    if (plan.status === CARE_PLAN_STATUS.SUPERSEDED || plan.status === CARE_PLAN_STATUS.ARCHIVED) {
      return { ok: false, error: `Cannot edit a ${plan.status} care plan` };
    }

    const secIdx = plan.sections.findIndex(s => s.sectionNumber === sectionNumber);
    if (secIdx === -1) return { ok: false, error: 'section_not_found' };

    const ts = _now();
    const before = { ...plan.sections[secIdx] };
    plan.sections[secIdx] = {
      ...plan.sections[secIdx],
      ...content,
      status: SECTION_STATUS.AMENDED,
      lastReviewedAt: ts,
      lastReviewedBy: userId,
    };
    plan.updatedAt = ts;
    plan.updatedBy = userId;
    plans[idx] = plan;
    this._saveAll(plans);

    this._audit('update_section', { planId: carePlanId, sectionNumber, userId, before: before.additionalNotes, after: plan.sections[secIdx].additionalNotes });
    return { ok: true, carePlan: plan };
  }

  // ── Section locking ───────────────────────────────────────────────────

  lockSection(carePlanId, sectionNumber, userId) {
    const locks = this._getLocks();
    const key = `${carePlanId}:${sectionNumber}`;
    const existing = locks[key];
    if (existing && existing.userId !== userId) {
      const age = Date.now() - new Date(existing.lockedAt).getTime();
      if (age < LOCK_TTL_MS) {
        return { ok: false, error: 'section_locked', lockedBy: existing.userId, lockedAt: existing.lockedAt };
      }
    }
    locks[key] = { userId, lockedAt: _now() };
    this._saveLocks(locks);
    return { ok: true };
  }

  unlockSection(carePlanId, sectionNumber, userId) {
    const locks = this._getLocks();
    const key = `${carePlanId}:${sectionNumber}`;
    const existing = locks[key];
    if (existing && existing.userId !== userId) {
      return { ok: false, error: 'locked_by_another_user' };
    }
    delete locks[key];
    this._saveLocks(locks);
    return { ok: true };
  }

  getSectionLock(carePlanId, sectionNumber) {
    const locks = this._getLocks();
    const key = `${carePlanId}:${sectionNumber}`;
    const lock = locks[key];
    if (!lock) return null;
    const age = Date.now() - new Date(lock.lockedAt).getTime();
    if (age >= LOCK_TTL_MS) {
      delete locks[key];
      this._saveLocks(locks);
      return null;
    }
    return lock;
  }

  // ── Workflow: sign-off ────────────────────────────────────────────────

  signOff(carePlanId, userId) {
    if (!userId) return { ok: false, error: 'userId is required for sign-off' };
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    if (![CARE_PLAN_STATUS.DRAFT, CARE_PLAN_STATUS.UNDER_REVIEW].includes(plan.status)) {
      return { ok: false, error: `Cannot sign-off a ${plan.status} care plan` };
    }

    const ts = _now();
    plans.forEach((cp, i) => {
      if (cp.residentId === plan.residentId && cp.status === CARE_PLAN_STATUS.ACTIVE &&
          cp.type === plan.type && cp.id !== carePlanId) {
        plans[i] = {
          ...cp,
          status: CARE_PLAN_STATUS.SUPERSEDED,
          supersededAt: ts,
          supersededReason: `Superseded by ${carePlanId}`,
          updatedAt: ts,
        };
      }
    });

    plans[idx] = {
      ...plan,
      status: CARE_PLAN_STATUS.ACTIVE,
      signedOffBy: userId,
      signedOffAt: ts,
      updatedAt: ts,
      updatedBy: userId,
    };

    this._saveAll(plans);
    this._audit('sign_off', { planId: carePlanId, userId, version: plan.version });
    return { ok: true, carePlan: plans[idx] };
  }

  coSign(carePlanId, userId) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };
    const ts = _now();
    plans[idx] = { ...plans[idx], coSignedBy: userId, updatedAt: ts };
    this._saveAll(plans);
    this._audit('co_sign', { planId: carePlanId, userId });
    return { ok: true, carePlan: plans[idx] };
  }

  // ── Workflow: review lifecycle ────────────────────────────────────────

  startReview(carePlanId) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };
    if (plans[idx].status !== CARE_PLAN_STATUS.ACTIVE) {
      return { ok: false, error: `Can only review an active plan (current: ${plans[idx].status})` };
    }
    const ts = _now();
    plans[idx] = { ...plans[idx], status: CARE_PLAN_STATUS.UNDER_REVIEW, updatedAt: ts };
    this._saveAll(plans);
    this._audit('start_review', { planId: carePlanId });
    return { ok: true, carePlan: plans[idx] };
  }

  completeReview(carePlanId, userId, outcome = REVIEW_OUTCOME.MINOR_UPDATE) {
    if (!userId) return { ok: false, error: 'userId is required' };
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };
    if (plans[idx].status !== CARE_PLAN_STATUS.UNDER_REVIEW) {
      return { ok: false, error: 'Care plan must be under_review to complete review' };
    }
    const ts = _now();
    const nextReview = new Date(Date.now() + REVIEW_INTERVAL_MS).toISOString();
    plans[idx] = {
      ...plans[idx],
      status: CARE_PLAN_STATUS.ACTIVE,
      version: (plans[idx].version || 1) + 1,
      lastReviewedAt: ts,
      lastReviewedBy: userId,
      reviewOutcome: outcome,
      reviewDueAt: nextReview,
      nextReviewDueAt: nextReview,
      updatedAt: ts,
      updatedBy: userId,
    };
    this._saveAll(plans);
    this._audit('complete_review', { planId: carePlanId, userId, version: plans[idx].version, outcome });
    return { ok: true, carePlan: plans[idx] };
  }

  getCarePlansNeedingReview() {
    const cutoff = new Date(Date.now() + REVIEW_LOOKAHEAD_MS).toISOString();
    return this._getAll().filter(
      cp => cp.status === CARE_PLAN_STATUS.ACTIVE && cp.reviewDueAt && cp.reviewDueAt <= cutoff,
    );
  }

  // ── Section evaluations ───────────────────────────────────────────────

  addSectionEvaluation(carePlanId, sectionNumber, evaluationData, userId) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    const secIdx = plan.sections.findIndex(s => s.sectionNumber === sectionNumber);
    if (secIdx === -1) return { ok: false, error: 'section_not_found' };

    const evaluation = createCarePlanEvaluation({
      ...evaluationData,
      sectionId: plan.sections[secIdx].id,
      reviewedBy: userId,
    });
    plan.sections[secIdx].evaluations = [...(plan.sections[secIdx].evaluations || []), evaluation];
    plan.sections[secIdx].lastReviewedAt = _now();
    plan.sections[secIdx].lastReviewedBy = userId;
    plan.sections[secIdx].status = SECTION_STATUS.REVIEWED;
    plan.updatedAt = _now();
    plans[idx] = plan;
    this._saveAll(plans);
    return { ok: true, evaluation, carePlan: plan };
  }

  // ── Goal management ───────────────────────────────────────────────────

  addGoal(carePlanId, sectionNumber, goalData) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    const secIdx = plan.sections.findIndex(s => s.sectionNumber === sectionNumber);
    if (secIdx === -1) return { ok: false, error: 'section_not_found' };

    const goal = createCarePlanGoal({ ...goalData, sectionId: plan.sections[secIdx].id });
    plan.sections[secIdx].goals.push(goal);
    plan.updatedAt = _now();
    plans[idx] = plan;
    this._saveAll(plans);
    return { ok: true, goal, carePlan: plan };
  }

  updateGoalStatus(carePlanId, goalId, status, userId, notes = '') {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    for (const sec of plan.sections) {
      const gIdx = sec.goals.findIndex(g => g.id === goalId);
      if (gIdx !== -1) {
        sec.goals[gIdx] = {
          ...sec.goals[gIdx],
          status,
          statusUpdatedAt: _now(),
          statusUpdatedBy: userId,
          statusNotes: notes,
        };
        plan.updatedAt = _now();
        plans[idx] = plan;
        this._saveAll(plans);
        this._audit('update_goal_status', { planId: carePlanId, goalId, status, userId });
        return { ok: true, goal: sec.goals[gIdx], carePlan: plan };
      }
    }
    return { ok: false, error: 'goal_not_found' };
  }

  addGoalProgress(carePlanId, goalId, progressData) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    for (const sec of plan.sections) {
      const gIdx = sec.goals.findIndex(g => g.id === goalId);
      if (gIdx !== -1) {
        const entry = createGoalProgress({ ...progressData, goalId });
        sec.goals[gIdx].progressEntries = [...(sec.goals[gIdx].progressEntries || []), entry];
        plan.updatedAt = _now();
        plans[idx] = plan;
        this._saveAll(plans);
        return { ok: true, progressEntry: entry, carePlan: plan };
      }
    }
    return { ok: false, error: 'goal_not_found' };
  }

  getAllGoals(carePlanId) {
    const plan = this.getCarePlan(carePlanId);
    if (!plan) return [];
    return plan.sections.flatMap(sec =>
      sec.goals.map(g => ({ ...g, sectionNumber: sec.sectionNumber, sectionTitle: sec.title }))
    );
  }

  // ── Intervention management ───────────────────────────────────────────

  addIntervention(carePlanId, sectionNumber, interventionData) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    const secIdx = plan.sections.findIndex(s => s.sectionNumber === sectionNumber);
    if (secIdx === -1) return { ok: false, error: 'section_not_found' };

    const intervention = createCarePlanIntervention({ ...interventionData, sectionId: plan.sections[secIdx].id });
    plan.sections[secIdx].interventions.push(intervention);
    plan.updatedAt = _now();
    plans[idx] = plan;
    this._saveAll(plans);
    return { ok: true, intervention, carePlan: plan };
  }

  discontinueIntervention(carePlanId, interventionId, reason, userId) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    for (const sec of plan.sections) {
      const iIdx = sec.interventions.findIndex(i => i.id === interventionId);
      if (iIdx !== -1) {
        sec.interventions[iIdx] = {
          ...sec.interventions[iIdx],
          status: INTERVENTION_STATUS.DISCONTINUED,
          discontinuedReason: reason,
          discontinuedDate: _now().slice(0, 10),
          discontinuedBy: userId,
        };
        plan.updatedAt = _now();
        plans[idx] = plan;
        this._saveAll(plans);
        return { ok: true, intervention: sec.interventions[iIdx], carePlan: plan };
      }
    }
    return { ok: false, error: 'intervention_not_found' };
  }

  getAllInterventions(carePlanId) {
    const plan = this.getCarePlan(carePlanId);
    if (!plan) return [];
    return plan.sections.flatMap(sec =>
      sec.interventions.map(i => ({ ...i, sectionNumber: sec.sectionNumber, sectionTitle: sec.title }))
    );
  }

  // ── Need management ───────────────────────────────────────────────────

  addNeed(carePlanId, sectionNumber, needData) {
    const plans = this._getAll();
    const idx = plans.findIndex(cp => cp.id === carePlanId);
    if (idx === -1) return { ok: false, error: 'care_plan_not_found' };

    const plan = plans[idx];
    const secIdx = plan.sections.findIndex(s => s.sectionNumber === sectionNumber);
    if (secIdx === -1) return { ok: false, error: 'section_not_found' };

    const need = createCarePlanNeed({ ...needData, sectionId: plan.sections[secIdx].id });
    plan.sections[secIdx].needs.push(need);
    plan.updatedAt = _now();
    plans[idx] = plan;
    this._saveAll(plans);
    return { ok: true, need, carePlan: plan };
  }

  // ── Auto-generation from assessments ──────────────────────────────────

  generateFromAssessments(residentId, createdBy = 'system') {
    const rid = String(residentId);
    const startTime = performance.now();

    const raw = LocalStorageService.get(ASSESSMENT_INSTANCES_KEY, {});
    const instances = Array.isArray(raw[rid]) ? raw[rid] : [];
    if (instances.length === 0) {
      debug('generateFromAssessments — no assessment instances for', rid);
      return { ok: false, error: 'no_assessment_instances' };
    }

    const result = this.createCarePlanRecord(rid, createdBy, CARE_PLAN_TYPE.COMPREHENSIVE);
    if (!result.ok) return result;

    const plan = result.carePlan;
    plan.generatedFrom = GENERATED_FROM.AUTO_ASSESSMENT;
    plan.assessmentInstanceIds = instances.map(i => i.id || i.payload?.id).filter(Boolean);
    const touchedSections = new Set();
    const ts = _now();

    instances.forEach(inst => {
      const slug = inst.payload?.slug || inst.payload?.tool || '';
      const sectionNumbers = ASSESSMENT_SECTION_MAP[slug] || [];

      sectionNumbers.forEach(secNum => {
        const secIdx = plan.sections.findIndex(s => s.sectionNumber === secNum);
        if (secIdx === -1) return;

        const section = plan.sections[secIdx];
        const name = inst.card?.name || inst.payload?.assessmentName || slug;
        const findings = inst.card?.findings || '';
        const recommendations = inst.card?.recommendations || '';
        const score = inst.card?.score;
        const riskLevel = inst.card?.riskLevel || inst.payload?.riskLevel;

        section.assessmentSources.push({
          assessmentId: inst.id, slug, name, score,
          completedAt: inst.completedAt || inst.card?.recordedAt,
        });

        if (riskLevel) {
          const levels = ['low', 'medium', 'high', 'very_high'];
          const currentIdx = levels.indexOf(section.riskLevel);
          const newIdx = levels.indexOf(riskLevel);
          if (newIdx > currentIdx) section.riskLevel = riskLevel;
        }

        if (findings) {
          section.needs.push(createCarePlanNeed({
            description: `${name}: ${findings}`,
            source: 'assessment_auto',
            sourceAssessmentId: inst.id,
            priority: riskLevel === 'high' || riskLevel === 'very_high' ? 'high' : 'medium',
          }));
        }

        if (recommendations) {
          section.goals.push(createCarePlanGoal({
            goalText: recommendations,
            goalType: riskLevel === 'high' || riskLevel === 'very_high' ? 'prevention' : 'maintenance',
            domain: this._sectionToDomain(secNum),
          }));
        }

        if (score != null) {
          section.clinicalAlerts = section.clinicalAlerts || [];
          if (riskLevel === 'high' || riskLevel === 'very_high') {
            const alertText = `${riskLevel.toUpperCase().replace('_', ' ')} — ${name} (Score: ${score})`;
            if (!section.clinicalAlerts.includes(alertText)) section.clinicalAlerts.push(alertText);
          }
        }

        const libInterventions = getDefaultInterventionsForSection(section.sectionKey, {
          riskLevel: riskLevel || section.riskLevel,
        });
        libInterventions.forEach(libEntry => {
          const alreadyExists = section.interventions.some(i => i.interventionText === libEntry.interventionText);
          if (!alreadyExists) {
            section.interventions.push(createCarePlanIntervention({
              interventionText: libEntry.interventionText,
              category: libEntry.category,
              frequency: libEntry.frequency,
              responsibleRole: libEntry.responsibleRole,
              source: INTERVENTION_SOURCE.INTERVENTION_LIBRARY,
              sourceInterventionId: libEntry.id,
              evidenceBasis: libEntry.evidenceBasis,
              isHighlighted: libEntry.isDefault,
            }));
          }
        });

        plan.sections[secIdx] = section;
        touchedSections.add(secNum);
      });
    });

    plan.generatedAt = ts;
    plan.updatedAt = ts;

    const plans = this._getAll();
    const planIdx = plans.findIndex(cp => cp.id === plan.id);
    if (planIdx !== -1) {
      plans[planIdx] = plan;
      this._saveAll(plans);
    }

    const elapsedMs = Math.round(performance.now() - startTime);
    const mappedSections = [...touchedSections].sort((a, b) => a - b);
    this._audit('auto_generate', { planId: plan.id, residentId: rid, mappedSections, instanceCount: instances.length, elapsedMs });
    debug(`generateFromAssessments completed in ${elapsedMs}ms`, { residentId: rid, mappedSections });
    return { ok: true, carePlan: plan, mappedSections, elapsedMs };
  }

  _sectionToDomain(sectionNumber) {
    const map = {
      1: 'social', 2: 'physical', 3: 'social', 4: 'physical', 5: 'safety',
      6: 'physical', 7: 'physical', 8: 'nutrition', 9: 'spiritual', 10: 'social',
      11: 'cognitive', 12: 'emotional', 13: 'pain', 14: 'continence', 15: 'physical',
      16: 'physical', 17: 'physical', 18: 'physical',
    };
    return map[sectionNumber] || 'physical';
  }

  // ── Interim Care Plan ─────────────────────────────────────────────────

  createInterimCarePlan(residentId, createdBy, immediateData = {}) {
    const result = this.createCarePlanRecord(residentId, createdBy, CARE_PLAN_TYPE.INTERIM);
    if (!result.ok) return result;

    const plan = result.carePlan;
    const ts = _now();

    const interimFields = ['allergies', 'diet', 'mobility', 'fallsRisk', 'medications',
      'communication', 'resuscitationStatus', 'immediateNeeds'];
    const activeKeys = interimFields.filter(k => immediateData[k]);

    if (immediateData.allergies) {
      const sec = plan.sections.find(s => s.sectionNumber === 2);
      if (sec) {
        sec.clinicalAlerts.push(`ALLERGY: ${immediateData.allergies}`);
        sec.additionalNotes = `Allergies: ${immediateData.allergies}`;
      }
    }
    if (immediateData.diet) {
      const sec = plan.sections.find(s => s.sectionNumber === 8);
      if (sec) sec.additionalNotes = `Diet: ${immediateData.diet}`;
    }
    if (immediateData.mobility) {
      const sec = plan.sections.find(s => s.sectionNumber === 4);
      if (sec) sec.additionalNotes = `Mobility: ${immediateData.mobility}`;
    }
    if (immediateData.fallsRisk) {
      const sec = plan.sections.find(s => s.sectionNumber === 5);
      if (sec) {
        sec.riskLevel = immediateData.fallsRisk;
        sec.additionalNotes = `Initial falls risk: ${immediateData.fallsRisk}`;
      }
    }
    if (immediateData.medications) {
      const sec = plan.sections.find(s => s.sectionNumber === 7);
      if (sec) sec.additionalNotes = `Medications: ${immediateData.medications}`;
    }
    if (immediateData.communication) {
      const sec = plan.sections.find(s => s.sectionNumber === 10);
      if (sec) sec.additionalNotes = `Communication: ${immediateData.communication}`;
    }
    if (immediateData.resuscitationStatus) {
      const sec = plan.sections.find(s => s.sectionNumber === 17);
      if (sec) {
        sec.clinicalAlerts.push(immediateData.resuscitationStatus);
        sec.additionalNotes = `Resuscitation status: ${immediateData.resuscitationStatus}`;
      }
    }

    plan.updatedAt = ts;
    const plans = this._getAll();
    const planIdx = plans.findIndex(cp => cp.id === plan.id);
    if (planIdx !== -1) { plans[planIdx] = plan; this._saveAll(plans); }

    this._audit('create_interim', { planId: plan.id, residentId, fields: activeKeys });
    return { ok: true, carePlan: plan };
  }

  // ── Behaviour Support Plan ────────────────────────────────────────────

  createBehaviourSupportPlan(residentId, createdBy, bspData = {}) {
    const result = this.createCarePlanRecord(residentId, createdBy, CARE_PLAN_TYPE.BEHAVIOUR_SUPPORT);
    if (!result.ok) return result;

    const plan = result.carePlan;
    plan.reviewDueAt = new Date(Date.now() + BSP_REVIEW_INTERVAL_WEEKS * 7 * 24 * 60 * 60 * 1000).toISOString();

    const sec11 = plan.sections.find(s => s.sectionNumber === 11);
    if (sec11 && bspData.behaviours) {
      sec11.additionalNotes = `Behaviours observed: ${bspData.behaviours}\n` +
        (bspData.triggers ? `Triggers: ${bspData.triggers}\n` : '') +
        (bspData.strategies ? `De-escalation strategies: ${bspData.strategies}\n` : '') +
        (bspData.unmetNeeds ? `Unmet needs analysis: ${bspData.unmetNeeds}` : '');

      if (bspData.restrictivePractices) {
        sec11.clinicalAlerts.push('RESTRICTIVE PRACTICE IN USE — see BSP details');
      }
    }

    plan.updatedAt = _now();
    const plans = this._getAll();
    const planIdx = plans.findIndex(cp => cp.id === plan.id);
    if (planIdx !== -1) { plans[planIdx] = plan; this._saveAll(plans); }

    this._audit('create_bsp', { planId: plan.id, residentId });
    return { ok: true, carePlan: plan };
  }

  // ── Palliative Care Plan ──────────────────────────────────────────────

  createPalliativeCarePlan(residentId, createdBy, palData = {}) {
    const result = this.createCarePlanRecord(residentId, createdBy, CARE_PLAN_TYPE.PALLIATIVE);
    if (!result.ok) return result;

    const plan = result.carePlan;
    const sec17 = plan.sections.find(s => s.sectionNumber === 17);
    if (sec17) {
      sec17.additionalNotes =
        (palData.resuscitationStatus ? `Resuscitation status: ${palData.resuscitationStatus}\n` : '') +
        (palData.phaseOfIllness ? `Phase: ${palData.phaseOfIllness}\n` : '') +
        (palData.preferredPlaceOfDeath ? `Preferred place of death: ${palData.preferredPlaceOfDeath}\n` : '') +
        (palData.anticipatoryMeds ? `Anticipatory medications: ${palData.anticipatoryMeds}\n` : '') +
        (palData.comfortPriorities ? `Comfort priorities: ${palData.comfortPriorities}` : '');

      if (palData.resuscitationStatus) {
        sec17.clinicalAlerts.push(palData.resuscitationStatus);
      }
      sec17.clinicalAlerts.push('PALLIATIVE PATHWAY ACTIVE');

      const libInterventions = getDefaultInterventionsForSection('palliative_eol', { palliativePathway: true });
      libInterventions.forEach(libEntry => {
        sec17.interventions.push(createCarePlanIntervention({
          interventionText: libEntry.interventionText,
          category: libEntry.category,
          frequency: libEntry.frequency,
          responsibleRole: libEntry.responsibleRole,
          source: INTERVENTION_SOURCE.INTERVENTION_LIBRARY,
          sourceInterventionId: libEntry.id,
          evidenceBasis: libEntry.evidenceBasis,
        }));
      });
    }

    plan.updatedAt = _now();
    const plans = this._getAll();
    const planIdx = plans.findIndex(cp => cp.id === plan.id);
    if (planIdx !== -1) { plans[planIdx] = plan; this._saveAll(plans); }

    this._audit('create_palliative', { planId: plan.id, residentId });
    return { ok: true, carePlan: plan };
  }

  // ── Summary Care Plan generation ──────────────────────────────────────

  generateSummaryCarePlan(carePlanId, residentData = {}) {
    const plan = this.getCarePlan(carePlanId);
    if (!plan) return { ok: false, error: 'care_plan_not_found' };

    const allAlerts = [];
    const extract = (sectionNumbers, field) => {
      return plan.sections
        .filter(s => sectionNumbers.includes(s.sectionNumber))
        .flatMap(s => {
          if (field === 'clinicalAlerts') return s.clinicalAlerts || [];
          if (field === 'goals') return (s.goals || []).map(g => g.goalText || g);
          if (field === 'needs') return (s.needs || []).map(n => n.description || n);
          if (field === 'interventions') return (s.interventions || [])
            .filter(i => i.status === INTERVENTION_STATUS.ACTIVE || !i.status)
            .filter(i => i.isHighlighted)
            .map(i => i.interventionText || i);
          if (field === 'residentPreferences') return s.residentPreferences ? [s.residentPreferences] : [];
          return [];
        });
    };

    allAlerts.push(...extract([2, 5, 6, 7, 11, 17], 'clinicalAlerts'));
    const resuscitationSec = plan.sections.find(s => s.sectionNumber === 17);
    if (resuscitationSec?.additionalNotes) {
      const match = resuscitationSec.additionalNotes.match(/Resuscitation status: (.+)/);
      if (match) allAlerts.push(match[1]);
    }

    const dietSection = plan.sections.find(s => s.sectionNumber === 8);
    const mobilitySection = plan.sections.find(s => s.sectionNumber === 4);
    const continenceSection = plan.sections.find(s => s.sectionNumber === 14);
    const commSection = plan.sections.find(s => s.sectionNumber === 10);
    const medSection = plan.sections.find(s => s.sectionNumber === 7);

    const summary = {
      id: _uid('SCP'),
      carePlanId: plan.id,
      residentId: plan.residentId,
      version: plan.version,
      status: plan.status,

      residentName: residentData.name || '',
      preferredName: residentData.preferredName || residentData.demographics?.preferredName || '',
      room: residentData.room || '',
      ward: residentData.ward || '',
      photo: residentData.photo || '',

      alerts: [...new Set(allAlerts)],

      diet: {
        summary: dietSection?.additionalNotes || '',
        preferences: dietSection?.residentPreferences || '',
        needs: (dietSection?.needs || []).map(n => n.description || n).slice(0, 3),
        interventions: (dietSection?.interventions || []).filter(i => i.isHighlighted || i.status === 'active').map(i => i.interventionText).slice(0, 3),
      },

      mobility: {
        summary: mobilitySection?.additionalNotes || '',
        needs: (mobilitySection?.needs || []).map(n => n.description || n).slice(0, 3),
        interventions: (mobilitySection?.interventions || []).filter(i => i.isHighlighted || i.status === 'active').map(i => i.interventionText).slice(0, 3),
      },

      continence: {
        summary: continenceSection?.additionalNotes || '',
        interventions: (continenceSection?.interventions || []).filter(i => i.isHighlighted || i.status === 'active').map(i => i.interventionText).slice(0, 3),
      },

      communication: {
        summary: commSection?.additionalNotes || '',
        strategies: (commSection?.interventions || []).filter(i => i.isHighlighted || i.status === 'active').map(i => i.interventionText).slice(0, 4),
      },

      preferences: extract([1, 3, 18], 'residentPreferences').slice(0, 4),

      keyMeds: {
        alerts: (medSection?.clinicalAlerts || []),
        interventions: (medSection?.interventions || []).filter(i => i.isHighlighted || i.status === 'active').map(i => i.interventionText).slice(0, 3),
      },

      goals: extract([1], 'goals').slice(0, 3),

      lastUpdated: plan.updatedAt,
      lastUpdatedBy: plan.updatedBy || plan.signedOffBy,
      reviewDueAt: plan.reviewDueAt,

      generatedAt: _now(),
    };

    debug('generateSummaryCarePlan', { carePlanId, alertCount: summary.alerts.length });
    return { ok: true, summary };
  }

  // ── Version diff ──────────────────────────────────────────────────────

  diffVersions(planIdA, planIdB) {
    const planA = this.getCarePlan(planIdA);
    const planB = this.getCarePlan(planIdB);
    if (!planA || !planB) return { ok: false, error: 'plan_not_found' };

    const changes = [];
    CARE_PLAN_SECTIONS.forEach(sectionDef => {
      const secA = planA.sections.find(s => s.sectionNumber === sectionDef.number) || {};
      const secB = planB.sections.find(s => s.sectionNumber === sectionDef.number) || {};

      const goalsA = (secA.goals || []).length;
      const goalsB = (secB.goals || []).length;
      const intvsA = (secA.interventions || []).length;
      const intvsB = (secB.interventions || []).length;
      const needsA = (secA.needs || []).length;
      const needsB = (secB.needs || []).length;

      if (goalsA !== goalsB || intvsA !== intvsB || needsA !== needsB ||
          secA.additionalNotes !== secB.additionalNotes || secA.riskLevel !== secB.riskLevel) {
        changes.push({
          sectionNumber: sectionDef.number,
          title: sectionDef.title,
          goalsChange: goalsB - goalsA,
          interventionsChange: intvsB - intvsA,
          needsChange: needsB - needsA,
          riskLevelBefore: secA.riskLevel,
          riskLevelAfter: secB.riskLevel,
          notesChanged: secA.additionalNotes !== secB.additionalNotes,
        });
      }
    });

    return { ok: true, changes, versionA: planA.version, versionB: planB.version };
  }

  // ── Facility dashboard KPIs ───────────────────────────────────────────

  getFacilityDashboard(facilityResidentIds = []) {
    const allPlans = this._getAll();
    const rids = new Set(facilityResidentIds.map(String));
    const facilityPlans = rids.size > 0
      ? allPlans.filter(cp => rids.has(String(cp.residentId)))
      : allPlans;

    const active = facilityPlans.filter(cp => cp.status === CARE_PLAN_STATUS.ACTIVE && cp.type === CARE_PLAN_TYPE.COMPREHENSIVE);
    const now = Date.now();
    const eightWeeksMs = REVIEW_INTERVAL_MS;

    const current = active.filter(cp => {
      if (!cp.lastReviewedAt && !cp.signedOffAt) return false;
      const lastDate = new Date(cp.lastReviewedAt || cp.signedOffAt).getTime();
      return (now - lastDate) <= eightWeeksMs;
    });

    const overdue = active.filter(cp => cp.reviewDueAt && new Date(cp.reviewDueAt).getTime() < now);

    const allGoals = active.flatMap(cp => cp.sections.flatMap(s => s.goals || []));
    const achievedGoals = allGoals.filter(g => g.status === GOAL_STATUS.ACHIEVED).length;
    const notAchievedGoals = allGoals.filter(g => g.status === GOAL_STATUS.NOT_ACHIEVED).length;

    const riskIncreased = [];
    active.forEach(cp => {
      cp.sections.forEach(sec => {
        const evals = sec.evaluations || [];
        const lastEval = evals[evals.length - 1];
        if (lastEval?.riskLevelChange === 'increased') {
          riskIncreased.push({ residentId: cp.residentId, sectionNumber: sec.sectionNumber, sectionTitle: sec.title });
        }
      });
    });

    const interimStillActive = facilityPlans.filter(cp =>
      cp.type === CARE_PLAN_TYPE.INTERIM && cp.status === CARE_PLAN_STATUS.ACTIVE &&
      (now - new Date(cp.createdAt).getTime()) > 48 * 3600 * 1000
    );

    const reviewsThisWeek = active.filter(cp => {
      if (!cp.lastReviewedAt) return false;
      const reviewDate = new Date(cp.lastReviewedAt).getTime();
      return (now - reviewDate) <= 7 * 24 * 60 * 60 * 1000;
    });

    const avgDaysSinceReview = active.length > 0
      ? Math.round(active.reduce((sum, cp) => {
          const last = cp.lastReviewedAt || cp.signedOffAt || cp.createdAt;
          return sum + (now - new Date(last).getTime()) / (24 * 3600 * 1000);
        }, 0) / active.length)
      : 0;

    return {
      totalActive: active.length,
      carePlansCurrent: active.length > 0 ? Math.round((current.length / active.length) * 100) : 0,
      carePlansOverdue: overdue.length,
      avgDaysSinceReview,
      reviewsThisWeek: reviewsThisWeek.length,
      goalsAchieved: achievedGoals,
      goalsNotAchieved: notAchievedGoals,
      totalGoals: allGoals.length,
      sectionsWithIncreasedRisk: riskIncreased,
      interimStillActiveOver48h: interimStillActive.length,
    };
  }

  // ── Audit trail retrieval ─────────────────────────────────────────────

  getAuditTrail(carePlanId = null) {
    const all = LocalStorageService.get(AUDIT_KEY, []);
    if (!carePlanId) return all;
    return all.filter(e => e.planId === carePlanId);
  }
}

export { CARE_PLAN_STATUS, CARE_PLAN_SECTIONS, CARE_PLAN_TYPE };
export default new CarePlanService();
